import React, { useState } from 'react';
import { Layout, Typography, Button, message } from 'antd';
import { ArrowRightOutlined, EditOutlined, ShareAltOutlined } from '@ant-design/icons';
import { ISiteVisit, ISiteVisitQuestion } from './types';
import ToolLayout from '../../components/Tools/ToolLayout';
import { SummaryMapView, QuestionCard } from './ui/Summary';
import { commonStyles } from './styles/common';
import { IMapController } from '../../lib/map';

const { Title } = Typography;
const { Content } = Layout;

interface SummaryProps {
  siteVisit: ISiteVisit;
  onEdit?: () => void;
  onNext?: () => void;
  showShare?: boolean;
}

const Summary: React.FC<SummaryProps> = ({ siteVisit, onEdit, onNext, showShare = true }) => {
  const [mapController, setMapController] = useState<IMapController | undefined>(undefined);

  const getAllQuestions = (): ISiteVisitQuestion[] => {
    return siteVisit.categories?.flatMap(category => category.questions || []) || [];
  };

  const handleShare = async () => {
    const currentUrl = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({
          title: siteVisit.map?.address || 'Site Visit',
          url: currentUrl,
        });
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(currentUrl);
        message.success('Link copied to clipboard');
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        message.error('Failed to copy link');
      }
    } else {
      message.error('Sharing not supported');
    }
  };

  const allQuestions = getAllQuestions();

  const mainContent = (
    <Content style={commonStyles.currentQuestionContent}>
      <div style={commonStyles.headerWithShare}>
        <Title level={5} style={commonStyles.titleWithShare}>
          {siteVisit.map?.address || 'Site Visit'}
        </Title>
        <div style={{ display: 'flex', gap: 8 }}>
          {onEdit && (
            <Button
              type="default"
              icon={<EditOutlined />}
              onClick={onEdit}
              style={commonStyles.shareButton}
            />
          )}
          {showShare && (
            <Button
              type="default"
              icon={<ShareAltOutlined />}
              onClick={handleShare}
              style={commonStyles.shareButton}
            />
          )}
        </div>
      </div>
      <SummaryMapView
        siteVisit={siteVisit}
        questions={allQuestions}
        onMapControllerReady={setMapController}
      />
    </Content>
  );

  const sidebarContent = (
    <div style={{ height: '100%', overflow: 'auto' }}>
      <Title level={5} style={{ margin: 8 }}>
        Summary
      </Title>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          margin: '0 8px',
          textAlign: 'left',
        }}>
        {allQuestions.map((question, index) => (
          <QuestionCard
            key={question.id || index}
            question={question}
            index={index + 1}
            mapController={mapController}
          />
        ))}
      </div>
    </div>
  );

  return (
    <ToolLayout
      hasModal={false}
      mainContent={mainContent}
      sidebarContent={sidebarContent}
      footerContent={
        onNext ? (
          <Button
            style={commonStyles.nextButton}
            icon={<ArrowRightOutlined />}
            onClick={onNext}>
            Next
          </Button>
        ) : null
      }
    />
  );
};

export default Summary;
